import ComponentPlaceholder from 'components/ComponentPlaceholder';
import ExpandableDescription from 'components/ExpandableDescription';
import { LayoutContainer } from 'components/Layout';
import ProductList from 'components/ProductList';
import Text from 'components/Text';
import { withRequiredProps } from 'hoc';
import { useIsEditing } from 'hooks';
import type { SitecoreBlockProps, SitecoreProductList } from 'models/sitecore';
import { getEditorMargin } from 'utils/business-logic';

interface Props extends SitecoreBlockProps {
	fields: SitecoreProductList;
}

function ProductListBlock({ fields, params }: Props) {
	const { brand, category, datasourceName, heading, total } = fields;
	const isEditing = useIsEditing();
	const categoryName = category?.name;
	const categoryText = category?.longText?.formatted;
	const editorMargin = getEditorMargin(
		// Force half margin for category pages, i.e. the product catalog.
		categoryName ? 'Half' : params?.margin,
		'Half',
	);

	if (isEditing && total === 0) {
		return (
			<ComponentPlaceholder
				componentName="ProductList"
				className={editorMargin}
			/>
		);
	}

	return (
		<LayoutContainer id={params?.anchor} outerClassName={editorMargin}>
			<Text as="h1" text={heading?.value || categoryName} />
			{categoryText && (
				<ExpandableDescription
					description={categoryText}
					textLengthBreakPoint={400}
					buttonVariant="text"
					buttonPlacement="left"
					textHeight="short"
					className="mt-6 max-w-col-6"
				/>
			)}

			<ProductList
				// This is deliberately a fallback stack rather than a template string
				// with every value. Having them all at the same time can result in the
				// list resetting when certain facet items are selected.
				key={category?.id || brand?.id.value || heading?.value}
				fields={fields}
				className="mt-r-xl"
				gtmItemListId={categoryName || brand ? 'category' : 'block'}
				gtmItemListName={
					categoryName
						? `Category: ${categoryName}`
						: brand
							? `Category: ${brand.title.value}`
							: `Block: ${datasourceName}`
				}
				placeholderComponentName="ProductList"
			/>
		</LayoutContainer>
	);
}
ProductListBlock.displayName = 'ProductListBlock';

export default withRequiredProps(ProductListBlock, 'fields');
